import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { TagDropdownSC } from "./user_details.styles.jsx";
import { ANAHEX_BE_HOST_NAME } from "../../../../../helpers/hostname.provider.js";
import WhatsappPhoneInput from "./whatsapp_number/whatsapp_number.component.jsx";

const API_HOST_NAME = ANAHEX_BE_HOST_NAME

const UserDetailsCard = ({ pos, leaving, onNext, currentUser, setUser, onOTPSend }) => {
    const { name, whatsappNumber, institution, designation, linkedinUrl, googleUser } = currentUser || {};

    const [isLoading, setLoading] = useState();
    const [error, setError] = useState();

    async function register(accessToken) {
        const response = await axios.post(
            `${API_HOST_NAME}/user/signup/register`,
            {
                whatsappNumber,
                designation,
                institution,
                linkedinUrl,
                authData: {
                    authProviderID: 'google.com',
                    authToken: accessToken
                }
            },
            {
                validateStatus: () => true // 👈 never throw for HTTP status
            }
        );

        return {
            status: response.status,
            data: response.data
        };
    }

    const onClick = async () => {
        if (isLoading || !googleUser) return;

        setLoading(true);
        setError(null);
        try {
            const status = await onOTPSend("recaptcha-container", googleUser);
            if (status.status) {
                if (status.alreadyLinked && status.accessToken) {
                    setUser({ ...currentUser, accessToken: status.accessToken })
                    const response = await register(status.accessToken);
                    // response?.data?.data?.accessToken
                    if (response.data?.successCode === 'VERIFIED') {
                        onNext();
                        onNext();
                    }
                } else {
                    onNext();
                }
                return;
            }
        } catch (err) {
            setError("Something went wrong. Please retry.");
        } finally {
            setLoading(false);
        }
    };

    const sanitisedUser = sanitizeUserInfo({ whatsappNumber, institution, linkedinUrl, designation })
    const isEnabled = pos === 1;
    const isAllowed = isEnabled && sanitisedUser.whatsappNumber && sanitisedUser.designation;

    return <div className={`content-card card-${pos} ${leaving ? "leaving" : ""}`}>
        <div className="signup-card-subtitle">
            Welcome {formatName(name)}
        </div>
        <div className="signup-card-description">
            Help us personalize your daily questions. A few details help us send the right system design question to your WhatsApp each day.
        </div>
        <DesignationDropdown currentUser={currentUser} setUser={setUser} isEnabled={isEnabled} />
        <WhatsappPhoneInput onChange={(number) => setUser({ ...currentUser, whatsappNumber: number })} />
        <div className="input-card">
            <label className="input-title" htmlFor="fromInput">Linkedin</label>
            <input id="fromInput" className="input-field" type="text"
                disabled={!isEnabled}
                onChange={(e) => setUser({ ...currentUser, linkedinUrl: e.target.value })} />
        </div>
        <div className="input-card">
            <label className="input-title" htmlFor="fromInput">Company / Institution</label>
            <input
                id="fromInput"
                className="input-field"
                type="text"
                disabled={!isEnabled}
                onChange={(e) => setUser({ ...currentUser, institution: e.target.value })}
            />
        </div>

        <div className={`next-button ${isAllowed ? 'allowed' : ''} ${isLoading ? 'loading' : ''}`}
            onClick={isAllowed && !isLoading ? onClick : null}>Send OTP</div>

        <div id="recaptcha-container"></div>
        <div className="error-message">{error}</div>
    </div>
}


const VALID_DESIGNATIONS = [
    "Student",
    "Software Developer",
    "Senior Software Developer",
    "Lead Software Developer",
    "Software Architect",
    "Engineering Leader",
    "DevOps Engineer",
    "Senior DevOps Engineer",
    "Lead DevOps Engineer",
    "QA Engineer",
    "Senior QA Engineer",
    "Lead QA Engineer",
    "Product Manager",
    "Senior Product Manager",
    "Other"
];


const DesignationDropdown = ({ currentUser, setUser, isEnabled }) => {
    const { designation } = currentUser || {};

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(designation || "Select");
    const ref = useRef(null);

    // Close on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleClick = () => setOpen(!open);

    return <TagDropdownSC ref={ref}>
        <label className="field-label">Designation*</label>

        <div className="select-box" onClick={isEnabled ? handleClick : null}>
            <span>{selected}</span>
            <span className="chevron">{open ? "▴" : "▾"}</span>
        </div>

        {open && (
            <div className="dropdown">
                {VALID_DESIGNATIONS.map((opt) => (
                    <div
                        key={opt}
                        className={`option-entry ${selected === opt ? "option-active" : ""}`}
                        onClick={() => {
                            setSelected(opt);
                            setOpen(false);
                            setUser({ ...currentUser, designation: opt })
                        }}
                    >
                        {opt}
                    </div>
                ))}
            </div>
        )}
    </TagDropdownSC>;
}


function formatName(name) {
    if (!name) return "";

    const firstWord = name.trim().split(/\s+/)[0];
    const capped =
        firstWord.charAt(0).toUpperCase() + firstWord.slice(1);

    return capped.slice(0, 20);
}


function sanitizeUserInfo({ whatsappNumber, institution, linkedinUrl, designation }) {
    const result = {};

    // WhatsApp number: + and 8–15 digits
    if (whatsappNumber && /^\+\d{8,15}$/.test(whatsappNumber)) {
        result.whatsappNumber = whatsappNumber;
    }

    // Institution: trimmed non-empty
    if (institution && institution.trim().length >= 2) {
        result.institution = institution.trim();
    }

    if (VALID_DESIGNATIONS.includes(designation))
        result.designation = designation;

    // LinkedIn URL: must be valid https linkedin.com
    if (linkedinUrl) {
        try {
            let urlString = linkedinUrl.trim();

            // If starts with http:// replace with https://
            if (/^http:\/\//i.test(urlString)) {
                urlString = urlString.replace(/^http:\/\//i, "https://");
            }

            // If no protocol at all, add https://
            if (!/^https?:\/\//i.test(urlString)) {
                urlString = "https://" + urlString;
            }

            const url = new URL(urlString);

            if (
                url.protocol === "https:" &&
                url.hostname.includes("linkedin.com")
            ) {
                result.linkedinUrl = urlString;
            }
        } catch (err) {
            console.error('Invalid linkedin url: ', err)
        }
    }


    return result;
}

export default UserDetailsCard;